import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Card,
  CardContent,
  Fade,
  useTheme,
  Tooltip,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  Error as ErrorIcon,
  Sync as SyncIcon
} from '@mui/icons-material';

import { useClaimService } from '../../services/claimService';
import type { ClaimRecord, ClaimDashboardStats } from '../../types';

const ClaimsPage: React.FC = () => {
  const theme = useTheme();
  const claimService = useClaimService();
  
  // Estados principales
  const [claims, setClaims] = useState<ClaimRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<ClaimRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Estados de paginación y filtros
  const [page, setPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  
  // Estados del dashboard
  const [dashboardStats, setDashboardStats] = useState<ClaimDashboardStats>({
    total: 0,
    pendientes: 0,
    sincronizados: 0,
    errores: 0
  });

  useEffect(() => {
    fetchClaims();
    fetchDashboardStats();
  }, [page, rowsPerPage, statusFilter]);

  const fetchClaims = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await claimService.getClaimsPaginated(
        page, 
        rowsPerPage, 
        statusFilter || undefined
      );
      setClaims(res.content);
      setTotalElements(res.totalElements);
    } catch (err) {
      console.error('Error fetching claims:', err);
      setError('Error al cargar las citas. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const stats = await claimService.getDashboardStats();
      setDashboardStats(stats as unknown as ClaimDashboardStats);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      // No mostrar error para stats, solo log
    }
  };

  const handleViewDetail = async (claim: ClaimRecord) => {
    setModalOpen(true);
    setModalLoading(true);
    try {
      const detail = await claimService.getClaimById(claim.id);
      setSelectedClaim(detail);
    } catch (error) {
      console.error('Error fetching claim detail:', error);
      setError('Error al cargar los detalles de la cita');
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedClaim(null);
    setError(null);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: 'success' | 'warning' | 'error' | 'info' | 'default' } = {
      'SINCRONIZADO': 'success',
      'PENDIENTE': 'warning',
      'ERROR': 'error'
    };
    return statusMap[status] || 'default';
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    const iconMap: { [key: string]: React.ReactElement } = {
      'SINCRONIZADO': <CheckCircleIcon sx={{ fontSize: 16 }} />,
      'PENDIENTE': <ScheduleIcon sx={{ fontSize: 16 }} />,
      'ERROR': <ErrorIcon sx={{ fontSize: 16 }} />
    };
    return iconMap[status] || <SyncIcon sx={{ fontSize: 16 }} />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700, 
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1
          }}
        >
          Gestión de Claims
        </Typography>
        <Typography variant="h6" color="text.secondary" fontWeight={400}>
          Administra y consulta todas las citas y servicios registrados
        </Typography>
      </Box>

      {/* Stats Cards - Dashboard Style */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarTodayIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Claims
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.sincronizados}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sincronizados
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.pendientes}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pendientes
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
              <ErrorIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                  {loading ? <CircularProgress size={24} /> : dashboardStats.errores}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Con Errores
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="primary" />
            Filtros de Claims
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                label="Estado"
                onChange={handleStatusFilterChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                  }
                }}
              >
                <MenuItem value="">Todos los estados</MenuItem>
                <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                <MenuItem value="SINCRONIZADO">Sincronizado</MenuItem>
                <MenuItem value="ERROR">Error</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Fade in={!!error}>
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Tabla de Claims - MODIFICADA */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)' }}>
          <Table size="small" stickyHeader aria-label="tabla de claims">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Claim ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Caso</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', align: 'center' }}>Origen</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} /> Cargando claims...
                  </TableCell>
                </TableRow>
              ) : claims.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box textAlign="center" py={{ xs: 4, sm: 6 }}>
                      <CalendarTodayIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        {statusFilter ? 'No se encontraron claims' : 'No hay claims registrados'}
                      </Typography>
                      <Typography variant="body1" color="text.disabled" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {statusFilter 
                          ? 'Intenta con otros filtros de búsqueda' 
                          : 'Los claims aparecerán aquí cuando se registren'
                        }
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                claims.map((claim) => (
                  <TableRow 
                    key={claim.id}
                    hover
                    sx={{ 
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        cursor: 'pointer'
                      }
                    }}
                  >
                    {/* Nombre del Cliente */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {claim.clientName || (claim.firstName && claim.lastName ? `${claim.firstName} ${claim.lastName}` : 'Sin nombre')}
                      </Typography>
                      
                    </TableCell>
                    
                    {/* Claim ID (solo external) */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight={claim.externalClaimId ? 600 : 400}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontFamily: claim.externalClaimId ? 'monospace' : 'inherit',
                          color: claim.externalClaimId ? 'success.main' : 'warning.main'
                        }}
                      >
                        {claim.externalClaimId || 'Sin sincronizar'}
                      </Typography>
                    </TableCell>
                    
                    {/* Caso (solo external referral ID) */}
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight={claim.externalReferralId || claim.referralExternalId ? 600 : 400}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          fontFamily: claim.externalReferralId || claim.referralExternalId ? 'monospace' : 'inherit',
                          color: claim.externalReferralId || claim.referralExternalId ? 'success.main' : 'info.main'
                        }}
                      >
                        {claim.externalReferralId || claim.referralExternalId || `REF-${claim.referralId}`}
                      </Typography>
                    </TableCell>
                    
                    {/* Estado */}
                    <TableCell>
                      <Chip 
                        label={claim.status} 
                        color={getStatusColor(claim.status)} 
                        size="small"
                        icon={getStatusIcon(claim.status)}
                        sx={{ 
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }}
                      />
                    </TableCell>
                    
                    {/* Origen */}
                    <TableCell align="center">
                      <Chip
                        label={(claim as any).origin === 'MANUAL' ? 'Manual' : (claim as any).origin === 'PHONE_CALL' ? 'Llamada Telefónica' : (claim as any).origin || 'N/A'}
                        color={(claim as any).origin === 'MANUAL' ? 'primary' : (claim as any).origin === 'PHONE_CALL' ? 'secondary' : 'default'}
                        size="small"
                        sx={{ 
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' }
                        }}
                      />
                    </TableCell>
                    
                    {/* Acciones */}
                    <TableCell>
                      <Tooltip title="Ver detalles" arrow>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetail(claim)}
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ 
            borderTop: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            borderRadius: '0 0 8px 8px'
          }}
        />
      </Paper>

      {/* Modal de Detalles */}
      <Dialog 
        open={modalOpen} 
        onClose={handleCloseModal} 
        fullWidth 
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white',
            position: 'relative'
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Detalle del Claim
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                {selectedClaim?.externalClaimId || 'Sin ID de Claim'}
              </Typography>
            </Box>
            <IconButton 
              onClick={handleCloseModal}
              sx={{ 
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
          {modalLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={40} />
            </Box>
          ) : selectedClaim ? (
            <>
              {/* Información Principal */}
              <Card sx={{ mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
                    Información del Claim
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Cliente:
                        </Typography>
                        <Typography variant="body2">
                          {selectedClaim.clientName || (selectedClaim.firstName && selectedClaim.lastName ? `${selectedClaim.firstName} ${selectedClaim.lastName}` : 'Sin nombre')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Claim ID:
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          {selectedClaim.externalClaimId || 'Sin sincronizar'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Caso ID:
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace">
                          {selectedClaim.externalReferralId || selectedClaim.referralExternalId || `REF-${selectedClaim.referralId}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Fecha de Servicio:
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(selectedClaim.dateOfServiceFrom)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Estado:
                        </Typography>
                        <Chip 
                          label={selectedClaim.status} 
                          color={getStatusColor(selectedClaim.status)} 
                          size="small"
                          icon={getStatusIcon(selectedClaim.status)}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Información de Sincronización */}
              <Card sx={{ bgcolor: 'grey.50', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary" fontWeight={600}>
                    Estado de Sincronización
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Sincronizado:
                        </Typography>
                        <Chip 
                          label={selectedClaim.isSynced ? 'Sí' : 'No'} 
                          color={selectedClaim.isSynced ? 'success' : 'warning'} 
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Intentos:
                        </Typography>
                        <Typography variant="body2">
                          {selectedClaim.syncAttempts}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                          Creado:
                        </Typography>
                        <Typography variant="body2">
                          {formatDateTime(selectedClaim.createdAt)}
                        </Typography>
                      </Box>
                      {selectedClaim.lastSyncAttempt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                            Último Sync:
                          </Typography>
                          <Typography variant="body2">
                            {formatDateTime(selectedClaim.lastSyncAttempt)}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                  
                  {selectedClaim.syncErrorMessage && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="error.dark" gutterBottom>
                        Error de Sincronización:
                      </Typography>
                      <Typography variant="body2" color="error.dark">
                        {selectedClaim.syncErrorMessage}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </>
          ) : null}
        </DialogContent>
        
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, bgcolor: 'grey.50' }}>
          <Button 
            onClick={handleCloseModal} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              minWidth: 100
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClaimsPage;